import { useCallback, useEffect, useRef, useState } from 'react'
import { loadDashboard, ActionItem } from '../services/dashboardService'
import './NotificationBell.css'

/**
 * Task notifications (Winnie's review, DD Review R2).
 *
 * Derived rather than stored. The dashboard already works out what needs a
 * person — payment runs waiting on approval, documents waiting on DD review,
 * overdue invoices — so the bell reads the same aggregation instead of
 * introducing a parallel notifications table that could drift out of step with
 * the actual work. Nothing here can say "you have 3 things" while the screens
 * show something different.
 *
 * It lives in the sidebar because the app's top header is hidden on desktop, so
 * there is no persistent header bar to hang it on.
 *
 * "Read" is per-browser: dismissing a count is a convenience, not an audit
 * record, and an item reappears the moment the underlying work does.
 */

const SEEN_KEY = 'directauto.notifications.seen'

export default function NotificationBell({ onNavigate }: { onNavigate: (view: string) => void }) {
  const [items, setItems] = useState<ActionItem[]>([])
  const [open, setOpen] = useState(false)
  const [seen, setSeen] = useState<string[]>(() => {
    try {
      return JSON.parse(localStorage.getItem(SEEN_KEY) || '[]')
    } catch {
      return []
    }
  })
  const boxRef = useRef<HTMLDivElement | null>(null)

  const refresh = useCallback(async () => {
    try {
      const snap = await loadDashboard()
      // Urgent items only — "coming up" and "handled" are not things anyone
      // needs to act on, and padding the badge with them makes it noise.
      setItems(snap.needsYou.filter((i) => i.severity === 'urgent' || i.severity === 'go'))
    } catch {
      // A failed refresh must never break the shell; the badge just goes quiet.
      setItems([])
    }
  }, [])

  useEffect(() => {
    refresh()
    const timer = window.setInterval(refresh, 5 * 60 * 1000)
    return () => window.clearInterval(timer)
  }, [refresh])

  useEffect(() => {
    const onDocClick = (e: MouseEvent) => {
      if (boxRef.current && !boxRef.current.contains(e.target as Node)) setOpen(false)
    }
    document.addEventListener('mousedown', onDocClick)
    return () => document.removeEventListener('mousedown', onDocClick)
  }, [])

  const unread = items.filter((i) => !seen.includes(i.key))

  const markAllSeen = () => {
    const keys = items.map((i) => i.key)
    setSeen(keys)
    try {
      localStorage.setItem(SEEN_KEY, JSON.stringify(keys))
    } catch {
      /* private browsing — the badge just stays */
    }
  }

  return (
    <div className="nb" ref={boxRef}>
      <button
        className={`nb-btn${unread.length ? ' has-unread' : ''}`}
        onClick={() => setOpen((v) => !v)}
        aria-label={unread.length ? `${unread.length} items need you` : 'Notifications'}
        title={unread.length ? `${unread.length} items need you` : 'Nothing needs you right now'}
      >
        <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.75" strokeLinecap="round" strokeLinejoin="round">
          <path d="M6 9a6 6 0 0 1 12 0c0 5 2.2 6 2.2 6H3.8S6 14 6 9z" />
          <path d="M10.4 20a1.7 1.7 0 0 0 3.2 0" />
        </svg>
        {unread.length > 0 && <span className="nb-badge">{unread.length > 9 ? '9+' : unread.length}</span>}
      </button>

      {open && (
        <div className="nb-panel" role="dialog">
          <div className="nb-panel-head">
            <strong>Needs you</strong>
            {items.length > 0 && (
              <button className="nb-mark" onClick={markAllSeen}>Mark all seen</button>
            )}
          </div>

          {items.length === 0 ? (
            <p className="nb-empty">Nothing is waiting on you right now.</p>
          ) : (
            <ul className="nb-list">
              {items.map((i) => (
                <li key={i.key}>
                  <button
                    className={`nb-item${seen.includes(i.key) ? ' is-seen' : ''}`}
                    onClick={() => {
                      onNavigate(i.view)
                      setOpen(false)
                    }}
                  >
                    <span className="nb-kicker" style={{ color: i.iconColor }}>{i.kicker}</span>
                    <span className="nb-title">{i.title}</span>
                    <span className="nb-detail">{i.detail}</span>
                  </button>
                </li>
              ))}
            </ul>
          )}

          <button className="nb-refresh" onClick={refresh}>Refresh</button>
        </div>
      )}
    </div>
  )
}
